import React, { useEffect, useRef } from "react";
import { Handle, Position, useViewport } from "@xyflow/react";
import { Tooltip } from "antd";
import { FileTextOutlined, ApartmentOutlined, PlusOutlined } from "@ant-design/icons";

const SwimlaneResizeHandle = ({ nodeId, onResize, direction }) => {
  const { zoom } = useViewport();
  const animationFrame = useRef(null);
  const cleanupListeners = useRef(null);

  useEffect(() => () => {
    cleanupListeners.current?.();
    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
  }, []);

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const lane = event.currentTarget.closest(".workflow-swimlane");
    if (!lane || !onResize) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const laneBounds = lane.getBoundingClientRect();
    const startWidth = laneBounds.width / zoom;
    const startHeight = laneBounds.height / zoom;
    let lastSize = direction === "horizontal" ? startWidth : startHeight;

    const resize = (pointerEvent) => {
      lastSize = direction === "horizontal"
        ? Math.min(2400, Math.max(500, startWidth + ((pointerEvent.clientX - startX) / zoom)))
        : Math.min(900, Math.max(140, startHeight + ((pointerEvent.clientY - startY) / zoom)));
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      animationFrame.current = requestAnimationFrame(() => {
        onResize(nodeId, direction === "horizontal"
          ? { width: Math.round(lastSize) }
          : { height: Math.round(lastSize) });
      });
    };

    const stopResize = () => {
      window.removeEventListener("pointermove", resize);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
      cleanupListeners.current = null;
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      animationFrame.current = requestAnimationFrame(() => onResize(nodeId, direction === "horizontal"
        ? { width: Math.round(lastSize) }
        : { height: Math.round(lastSize) }));
    };

    cleanupListeners.current = stopResize;
    window.addEventListener("pointermove", resize);
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);
  };

  return (
    <div
      role="separator"
      aria-label={direction === "horizontal" ? "Cambiar ancho del carril" : "Cambiar altura del carril"}
      aria-orientation={direction === "horizontal" ? "vertical" : "horizontal"}
      className={`nodrag nopan workflow-swimlane__resize-control workflow-swimlane__resize-control--${direction}`}
      onPointerDown={startResize}
    />
  );
};

const WorkflowNode = ({ id, data, type, selected }) => {
  const nodeType = type || "actividad";
  const shape = data?.shape || (
    nodeType === "carril" ? "swimlane" :
    nodeType === "decision" ? "diamond" :
    nodeType === "inicio" ? "start" :
    nodeType === "fin" ? "end" :
    nodeType === "documento" ? "database" :
    "rectangle"
  );
  const colorRole = data?.colorRole || "neutral";
  const isDecision = shape === "diamond";
  const isTerminal = shape === "start" || shape === "end";
  const linkedControl = data?.controlCode
    ? { code: data.controlCode, title: data.controlTitle || data.controlCode }
    : null;
  const linkedRisk = linkedControl && data?.riskTitle
    ? { id: data.riskId, title: data.riskTitle }
    : null;
  const customBackground = data?.backgroundColor;
  const customBorder = data?.borderColor;
  const customTextColor = (() => {
    if (!customBackground) return undefined;
    if (customBackground === "transparent") return "#1d2939";
    const hex = customBackground.replace("#", "");
    if (!/^[0-9a-f]{6}$/i.test(hex)) return undefined;
    const [red, green, blue] = [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16));
    return ((red * 299) + (green * 587) + (blue * 114)) / 1000 < 145 ? "#ffffff" : "#1d2939";
  })();
  const customStyle = {
    ...(customBackground ? { background: customBackground } : {}),
    ...(customBorder ? {
      borderColor: customBorder,
      "--workflow-node-border-color": customBorder,
    } : {}),
    ...(customTextColor ? { color: customTextColor } : {}),
  };

  if (shape === "swimlane") {
    return (
      <div className={`workflow-swimlane ${selected ? "is-selected" : ""}`}>
        {selected && data?.canResize && (
          <>
            <SwimlaneResizeHandle nodeId={id} onResize={data?.onResizeSwimlane} direction="vertical" />
            <SwimlaneResizeHandle nodeId={id} onResize={data?.onResizeSwimlane} direction="horizontal" />
          </>
        )}
        <div className="workflow-swimlane__label">
          <span>{data?.label || "Carril"}</span>
        </div>
        <div className="workflow-swimlane__body" />
      </div>
    );
  }

  return (
    <div className={`workflow-node-wrapper workflow-node-wrapper--${shape}`}>
      {linkedControl && (
        <div className="workflow-node__controls-list">
          <Tooltip title={linkedControl.title}>
            <button
              type="button"
              className="nodrag nopan workflow-node__control-circle"
              aria-label={`Control: ${linkedControl.title}`}
              disabled={!data?.canAddControl}
              onMouseDown={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                data.onAddControl?.(id, linkedControl.code);
              }}
            >
              <span>{linkedControl.title}</span>
            </button>
          </Tooltip>
          {linkedRisk && (
            <Tooltip title={`Riesgo: ${linkedRisk.title}`}>
              <div
                className="nodrag nopan workflow-node__risk-circle"
                aria-label={`Riesgo: ${linkedRisk.title}`}
              >
                <span>{linkedRisk.title}</span>
              </div>
            </Tooltip>
          )}
        </div>
      )}
      <div
        className={`workflow-node workflow-node--${shape} workflow-node--role-${colorRole} ${selected ? "is-selected" : ""}`}
        style={customStyle}
      >
        {data?.canAddControl && (
          <Tooltip title={linkedControl ? "Cambiar o quitar control" : "Agregar control"}>
            <button
              type="button"
              className="nodrag nopan workflow-node__add-control"
              aria-label="Agregar control"
              onMouseDown={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                data.onAddControl?.(id, linkedControl?.code || null);
              }}
            >
              <PlusOutlined />
            </button>
          </Tooltip>
        )}
        <Handle type="target" position={Position.Top} id="top-target" />
        <Handle type="target" position={Position.Left} id="left-target" />
        <div className={isDecision ? "workflow-node__diamond-content" : "workflow-node__content"}>
          {["database", "system-application", "text", "document", "multi-document"].includes(shape) && <FileTextOutlined className="workflow-node__icon" />}
          {nodeType === "subproceso" && <ApartmentOutlined className="workflow-node__icon" />}
          <span>{data?.label || (isTerminal ? shape.toUpperCase() : "Sin título")}</span>
        </div>
        <Handle type="source" position={Position.Right} id="right-source" />
        <Handle type="source" position={Position.Bottom} id="bottom-source" />
      </div>
    </div>
  );
};

export default WorkflowNode;
